import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  FiAlertCircle,
  FiArrowRight,
  FiCheckSquare,
  FiEye,
  FiEyeOff,
  FiFileText,
  FiLock,
  FiMail,
  FiTrendingUp,
  FiTruck,
  FiUsers,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { RoutesPaths } from "../Router/constant";
import { loginAPI, loginPartnerAPI } from "../Services/authService";

const VARIANTS = {
  admin: {
    login: loginAPI,
    panelClass: "qsi-panel-admin",
    logo: "/assets/img/quimeraLogo.svg",
    eyebrow: "Admin portal",
    panelTitle: "Hi, welcome back",
    panelEmoji: "👋",
    panelText: "Manage bookings, vendors and customers more effectively with optimised workflows.",
    panelImage: "/assets/img/GlobalLogisticsAdmin.png",
    features: [
      { Icon: FiTrendingUp, title: "Track every booking end to end", text: "Real-time visibility across all shipments." },
      { Icon: FiUsers, title: "Assign vendors in a few clicks", text: "Simplify vendor management." },
      { Icon: FiFileText, title: "Quotations, documents and chat in one place", text: "Everything you need, all in one platform." },
    ],
    title: "Welcome back",
    subtitle: "Please enter your details to sign in to the admin panel.",
    // The reset flow (ResetPassword) only supports vendor accounts.
    resetLink: false,
  },
  partner: {
    login: loginPartnerAPI,
    panelClass: "qsi-panel-partner",
    logo: "/assets/img/quimeraLogoPartner.svg",
    eyebrow: "Vendor portal",
    panelTitle: "Vendor Portal",
    panelEmoji: "",
    panelText: "Manage bookings, contracts, and services with ease.",
    panelImage: "/assets/img/GlobalLogisticspartner.png",
    features: [
      { Icon: FiCheckSquare, title: "Accept booking requests", text: "Respond to new bookings as they arrive." },
      { Icon: FiFileText, title: "Share quotations and documents", text: "Send quotes and files to the Quimera team." },
      { Icon: FiTruck, title: "Update shipping status in real time", text: "Keep every shipment on track." },
    ],
    title: "Sign in",
    subtitle: "Sign in to access your vendor dashboard.",
    resetLink: true,
  },
};

/*
 * Shared sign-in screen.
 *  variant="admin"   -> Signin (admin panel)
 *  variant="partner" -> PartnerSignin (vendor portal)
 */
export default function SignInForm({ variant = "admin" }) {
  const config = VARIANTS[variant] || VARIANTS.admin;
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      navigate(RoutesPaths.DASHBOARD);
      return;
    }

    const rememberedEmail = localStorage.getItem("rememberMeEmail");
    const rememberedPassword = localStorage.getItem("rememberMePassword");
    if (rememberedEmail && rememberedPassword) {
      setEmail(rememberedEmail);
      setPassword(rememberedPassword);
      setRememberMe(true);
    }
  }, [navigate]);

  const handleLogin = async (event) => {
    event?.preventDefault();
    if (loading) return;

    setError("");
    if (!email || !password) {
      return setError("Please enter email and password.");
    }

    setLoading(true);
    try {
      const res = await config.login({ email, password });
      if (res.data?.data) {
        const token = res.data.data;

        if (rememberMe) {
          localStorage.setItem("token", token);
          localStorage.setItem("rememberMeEmail", email);
          localStorage.setItem("rememberMePassword", password);
        } else {
          localStorage.setItem("token", token);
          localStorage.removeItem("rememberMeEmail");
          localStorage.removeItem("rememberMePassword");
        }

        toast.success("Welcome back!");
        navigate(RoutesPaths.DASHBOARD);
      } else {
        setError("Invalid login response");
      }
    } catch (err) {
      setError(err.response?.data?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => error && setError("");

  return (
    <div className={`qsi-page qsi-${variant}`}>
      {/* Brand panel */}
      <aside className={`qsi-panel ${config.panelClass}`}>
        <img src={config.logo} alt="Quimera Logistics" className="qsi-panel-logo" />

        <div className="qsi-panel-copy">
          <span className="qsi-eyebrow">{config.eyebrow}</span>
          <h1>
            {config.panelTitle}
            {config.panelEmoji && (
              <span className="qsi-wave" aria-hidden="true">
                {" "}
                {config.panelEmoji}
              </span>
            )}
          </h1>
          <p>{config.panelText}</p>
          <ul className="qsi-features">
            {config.features.map(({ Icon, title, text }) => (
              <li key={title}>
                <span className="qsi-feature-icon" aria-hidden="true">
                  <Icon />
                </span>
                <span>
                  <strong>{title}</strong>
                  <small>{text}</small>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="qsi-panel-art" aria-hidden="true">
          <img src={`${process.env.PUBLIC_URL}${config.panelImage}`} alt="" />
        </div>
      </aside>

      {/* Form */}
      <main className="qsi-main">
        <img src="/assets/img/quimeraLogo.svg" alt="Quimera Logistics" className="qsi-mobile-logo" />

        <form className="qsi-card" onSubmit={handleLogin} noValidate>
          <h2 className="qsi-title">{config.title}</h2>
          <p className="qsi-subtitle">{config.subtitle}</p>

          {error && (
            <div className="qsi-error" role="alert">
              <FiAlertCircle aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <label className="qsi-label" htmlFor="qsi-email">
            Email
          </label>
          <div className="qsi-input">
            <FiMail aria-hidden="true" />
            <input
              id="qsi-email"
              type="email"
              autoComplete="username"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearError();
              }}
            />
          </div>

          <label className="qsi-label" htmlFor="qsi-password">
            Password
          </label>
          <div className="qsi-input">
            <FiLock aria-hidden="true" />
            <input
              id="qsi-password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                clearError();
              }}
            />
            <button
              type="button"
              className="qsi-eye"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>

          <div className="qsi-row">
            <label className="qsi-remember" htmlFor="qsi-remember">
              <input
                type="checkbox"
                id="qsi-remember"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              Remember me
            </label>

            {config.resetLink && (
              <NavLink to={RoutesPaths.RESET_PASSWORD} className="qsi-link">
                Reset password?
              </NavLink>
            )}
          </div>

          <button type="submit" className="qsi-submit" disabled={loading} aria-busy={loading}>
            {loading ? (
              <>
                <span className="qo-spinner" aria-hidden="true" /> Signing in...
              </>
            ) : (
              <>
                Sign in <FiArrowRight aria-hidden="true" />
              </>
            )}
          </button>
        </form>

        <p className="qsi-footer">© Quimera {new Date().getFullYear()}. All rights reserved.</p>
      </main>
    </div>
  );
}
